import { Helmet } from "@dr.pogodin/react-helmet";
import type { BlogPostWithRelations } from "@/types/blog";

interface BlogSEOHeadProps {
  post: BlogPostWithRelations;
}

// Build-time generator (scripts/generate-og-images.mjs) emits a branded
// 1200×630 PNG to /og-images/{slug}.png for every published post. Used as
// the fallback when an editor hasn't uploaded a custom og_image_url.
const generatedOgImage = (slug: string) =>
  `https://lembra.com.au/og-images/${slug}.png`;

export function BlogSEOHead({ post }: BlogSEOHeadProps) {
  const title = post.meta_title || post.title;
  const description = post.meta_description || post.excerpt || "";
  const image =
    post.og_image_url || post.cover_image_url || generatedOgImage(post.slug);
  const url = `https://lembra.com.au/blog/${post.slug}`;
  const authorName = post.blog_authors?.name || "Lembra";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || "",
    image,
    author: {
      "@type": "Organization",
      name: authorName,
    },
    publisher: {
      "@type": "Organization",
      name: "Lembra",
      logo: {
        "@type": "ImageObject",
        url: "https://lembra.com.au/logo.svg",
      },
    },
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at,
    mainEntityOfPage: url,
  };

  // MS181: BreadcrumbList for the /blog/<slug> trail. Helps Google
  // show the "Lembra › Blog › <Title>" breadcrumb display in SERPs
  // instead of the raw URL. Same shape as the Vendor.tsx breadcrumb.
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Lembra",
        item: "https://lembra.com.au",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://lembra.com.au/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: url,
      },
    ],
  };

  return (
    <Helmet>
      <title>{title} | Lembra Blog</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      {post.published_at && <meta property="article:published_time" content={post.published_at} />}
      <meta property="article:author" content={authorName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
    </Helmet>
  );
}
